import { useNavigate } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { useAuthStore } from '../../../../../store';
import React, { useEffect, useState } from 'react';
import { usePurchaseRequestStore } from '../../../../../store/PurchaseRequestStore';
import { useCreateData, useFetchData } from '../../../../../hooks';
import { useUploadPRFiles } from '../../../../../hooks/usePurchaseRequestHooks';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import CustomForm from '../../../../../components/ui/CustomForm';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import { PurchaseRequestForm } from '../../../../../types/PurchaseRequest';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import RequestLineCreatePage from './RequestLineCreatePage';

const PurchaseRequestCreatePage = () => {
  const api = '/pr/requests';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const purchaseRequestStore = usePurchaseRequestStore();
  const { user } = useAuthStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');
  const [fileList, setFileList] = useState<FormData | null>(null);

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const {
    mutate,
    isPending,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
    data: createdDate,
  } = useCreateData(api);

  const {
    mutate: uploadMutate,
    isError: isErrorUpload,
    error: errorUpload,
  } = useUploadPRFiles();

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/purchase-request');
  };

  useEffect(() => {
    if (isSuccessCreate) {
      if (fileList !== null) {
        uploadMutate({ id: createdDate.data.id, formData: fileList });
      }
      setIsInfoModalOpen(true);
    }
    if (isErrorUpload) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpload.message);
    }
    if (isErrorCreate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorCreate.message);
    }
  }, [isSuccessCreate, isErrorUpload]);

  const handleClose = () => {
    navigate('/purchase-request');
  };

  const { data: buyerData, isLoading: isBuyerLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/buyers', { resource: Resource.NAME, length: 999999 });

  const fields: FormField[] = [
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: purchaseRequestStore.formData.requiredDate,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: purchaseRequestStore.formData.source,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue:
        purchaseRequestStore.formData.destination != null
          ? purchaseRequestStore.formData.destination.id
          : user?.defaultLocation?.id,
    },
    {
      name: 'requestedBy',
      label: 'Requested by',
      type: 'input',
      defaultValue: purchaseRequestStore.formData.requestedBy,
    },
    {
      name: 'buyer',
      label: 'Buyer',
      allowClear: true,
      isMultiple: false,
      type: 'select',
      isLoading: isBuyerLoading,
      values: buyerData?.data,
      defaultValue: purchaseRequestStore.formData.buyer?.id,
    },
    {
      name: 'dateNeeded',
      label: 'Date needed',
      type: 'datepicker',
      defaultValue: purchaseRequestStore.formData.dateNeeded,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      defaultValue: purchaseRequestStore.formData.source,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: purchaseRequestStore.formData.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
    },
  ];

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const payload: PurchaseRequestForm = {
      ...values,
      destination: destination,
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      buyer: buyerData?.data.find((b) => b.id === values.buyer),
      dateNeeded: values.dateNeeded
        ? dayjs(values.dateNeeded).format(dateFormat)
        : null,
      requestLines: purchaseRequestStore.formData.requestLines || [],
    };
    mutate(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      setFileList(formData);
    }
  };

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Purchase Request'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        <div className="p-4">
          <CustomForm
            data={purchaseRequestStore.formData}
            formFields={fields}
            form={form}
            onFinish={onFinish}
            rowCol={3}
            layout={'vertical'}
            formName={'po_form'}
          />
        </div>
        <RequestLineCreatePage />
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Purchase order successfully created'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseRequestCreatePage;
